// Catalog.ID Client — Delegate activation reference (registration-restructure
// §13.5, amended by §14.2)
// ============================================================================
// What an activation link identifies: a SIGNED invitation, named by the
// 16-character CODE printed on its card (M18) — the only form there is since
// M20 retired the pre-M15 bearer `?token=` link. Pure; the entry page, the
// router, the resume banner and the wizard all share it.
//
// The code — not a lookup id — is what travels here. The lookup id is a
// derivative (one 32 MiB scrypt away, `deriveInvitationSecrets`), so keeping the
// code is what lets a resumed wizard re-derive both it and the fixed-value salt
// without ever asking the invitee to type the code again. The stash therefore
// holds the code too; that is deliberate and no worse than the card in their
// pocket, and §3.3 already says the onboarding stash is unencrypted on purpose.

import {
    normaliseInvitationCode, normaliseOrgCid, parseInvitationUrl, formatInvitationCode, INVITATION_ENTRY_PATH,
} from './invitation-document';
import { withDashboardPort } from '../state/dashboard-context';
import { lookupOrigin, type IdentityOrigin, type OriginLookup } from '../state/origins';
import type { OnboardingStash } from './onboarding-stash';

export interface DelegateInvitationRef {
    /** Canonical invitation code, `INV-` + 16 body characters (M18). */
    code?: string;
    /** The organization the invitation belongs to (docs/cid-governance.md M7a):
     *  from the link's `org=`, the typed Organization ID, or `inspect`. */
    orgCid?: string;
}

/** Read the activation reference from a URL; null when it names nothing. */
export function activationRefFromUrl(href: string): DelegateInvitationRef | null {
    const signed = parseInvitationUrl(href);
    if (!signed) return null;
    return signed.orgCid ? { code: signed.code, orgCid: signed.orgCid } : { code: signed.code };
}

/** The reference an in-flight activation stash was started with. */
export function activationRefFromStash(stash: OnboardingStash | null): DelegateInvitationRef | null {
    if (!stash || stash.registrationType !== 'delegate-activation') return null;
    if (!stash.invitationCode) return null;
    const orgCid = normaliseOrgCid(stash.shared?.['invitationOrgCid'] as string | undefined);
    return orgCid ? { code: stash.invitationCode, orgCid } : { code: stash.invitationCode };
}

/**
 * True when the stash belongs to THIS invitation (re-opening the link resumes).
 * Compared on the canonical code, so a link typed in one grouping and stashed in
 * another still matches.
 */
export function activationRefMatchesStash(ref: DelegateInvitationRef, stash: OnboardingStash | null): boolean {
    const stashed = activationRefFromStash(stash);
    if (!stashed || ref.code === undefined || stashed.code === undefined) return false;
    return normaliseInvitationCode(stashed.code) === normaliseInvitationCode(ref.code);
}

/**
 * The URL a printed invitation card carries — the one that must open the app
 * wherever the invitee is. Built on the node's announced standalone site
 * (`siteBaseUrl`, the nginx vhost in production); when the node announces
 * none (dev), on the dashboard helper origin this client runs on, carrying
 * `dp` so the fresh tab learns the real server URL (§11 trap).
 *
 * It points at the anonymous ENTRY page, not at the activation wizard: the entry
 * page routes on the code's prefix, so one address serves invitation codes and
 * voucher codes alike (§14.2).
 */
export function activationUrlFor(
    ref: DelegateInvitationRef,
    site: { siteBaseUrl: string | null | undefined; origin: string; dashboardPort: number | undefined },
): string {
    const { path, search, hash } = activationRefLink(ref);
    const query = search ? `?${search}` : '';
    const fragment = hash ? `#${hash}` : '';
    if (site.siteBaseUrl) return `${site.siteBaseUrl.replace(/\/+$/, '')}${path}${query}${fragment}`;
    // `dp` must land in the QUERY (the boot code reads searchParams), so it is
    // appended before the fragment.
    return `${site.origin}${withDashboardPort(`${path}${query}`, site.dashboardPort)}${fragment}`;
}

/**
 * The three parts of an activation link for `ref`: the path, what goes in the
 * query, and what goes in the fragment.
 *
 * A code goes in the FRAGMENT of the entry page — browsers never send it, so it
 * reaches no access log and no Referer, which is what keeps the node's promise
 * that it never sees the code even when somebody clicks the link.
 */
export function activationRefLink(ref: DelegateInvitationRef): { path: string; search: string; hash: string } {
    if (ref.code) {
        const code = normaliseInvitationCode(ref.code);
        if (code) {
            // The organization rides beside the code (M7a): the invitation lives
            // only at the organization's origin, which begin must be sent to.
            const orgCid = normaliseOrgCid(ref.orgCid);
            return {
                path: INVITATION_ENTRY_PATH,
                search: '',
                // The DISPLAY grouping, because this is also what a human reads
                // off the card and retypes; the parser normalises either way.
                hash: `code=${formatInvitationCode(code)}${orgCid ? `&org=${orgCid}` : ''}`,
            };
        }
    }
    return { path: INVITATION_ENTRY_PATH, search: '', hash: '' };
}

/**
 * Where an organization's invitations are redeemed: its origin node
 * (docs/cid-governance.md D18, M7a), asked of the entry node. `begin` and
 * `activate` are sent there, and the new delegate's sessions live there. Throws
 * a sentence the invitee can act on when there is nowhere to send them.
 */
export async function organizationOrigin(
    orgCid: string, lookup: (cid: string) => Promise<OriginLookup> = (cid) => lookupOrigin(cid),
): Promise<IdentityOrigin> {
    let answer: OriginLookup;
    try {
        answer = await lookup(orgCid);
    } catch {
        throw new Error('Catalog.ID could not be reached. Check your connection and try again.');
    }
    if (answer.kind === 'found') return answer.origin;
    if (answer.kind === 'unreachable') {
        throw new Error(`The organization's home server (${answer.code}) cannot be reached right now. `
            + 'Please try again later.');
    }
    throw new Error('No organization has that Organization ID. Check it against the one printed on your card.');
}
